import React from "react";

import { css, cx, keyframes } from "@emotion/css";

import { useStyles } from "@/hooks/useStyles";
import { Theme } from "@/theme/CreateTheme.ts";
import { PaletteColors } from "@/theme/PaletteOverrides.ts";
import { AtomProps } from "@/uiKit/atoms/utils/AtomTypes.ts";

export type LoaderSize = "S" | "M" | "L";

export type BaseLoaderProps = {
  size: LoaderSize;
  thickness?: number;
  color?: PaletteColors;
};

export type LoaderProps = AtomProps<BaseLoaderProps>;

/**
 * Loader is a functional React component that renders a customizable loading spinner.
 *
 * The loader is a circular element with a part that spins.
 *
 * The loader can be customized with the following props:
 * - size: specifies the size of the loader. Can accept predefined size keys.
 * - thickness: determines the thickness of the loader. Defaults to 1.
 * - color: defines the color of the loader. Defaults to 'primary'.
 *
 */
export const Loader: React.FC<LoaderProps> = ({ size, thickness = 1, color = "primary", className, ...props }) => {
  const styles = useStyles(makeStyles, SizeMatch[size], thickness, color);

  return (
    <div
      className={cx(styles.loader, className)}
      aria-label="loader"
      aria-busy="true"
      role="progressbar"
      {...props}
    ></div>
  );
};

const SizeMatch: { [K in LoaderSize]: (theme: Theme) => string } = {
  S: theme => theme.spacing(8),
  M: theme => theme.spacing(12),
  L: theme => theme.spacing(20),
};

const makeStyles = (theme: Theme, size: (theme: Theme) => string, thickness: number, color: PaletteColors) => ({
  loader: css({
    color: theme.palette[color].light,
    height: size(theme),
    width: size(theme),
    border: `calc(${size(theme)} / 10 * ${thickness}) solid color-mix(in srgb, currentColor 10%, transparent)`,
    borderTop: `calc(${size(theme)} / 10 * ${thickness}) solid currentColor`,
    borderRadius: "50%",
    animation: `${spin} 1s cubic-bezier(0.8, 0.3, 0.2, 0.7) infinite`,
  }),
});

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }  
`;
